import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { useMemo } from 'react'

import { fetchDelegations, fetchStakingParams } from '@/api'
import CopyWithTooltip from '@/common/CopyWithTooltip'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  emitter,
  ErrorHandler,
  formatAddress,
  formatBridgeAtomic,
  formatDurationString,
  isPositiveDecimalAmount,
} from '@/helpers'
import { buildUndelegateNftMsg, UndelegateError } from '@/libs/keplr/messages'
import { invalidateNftScope } from '@/queries/invalidate'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

type Props = {
  open: boolean
  nftAddress: string
  onClose: () => void
}

export default function UndelegateModal({ open, nftAddress, onClose }: Props) {
  const { address, signAndBroadcast } = useKeplrWallet()
  const queryClient = useQueryClient()

  // === FETCH DELEGATION ===
  const { data: delegationData, isLoading } = useQuery({
    queryKey: queryKeys.staking.delegations(nftAddress),
    enabled: Boolean(open && nftAddress),
    queryFn: async () => fetchDelegations(nftAddress),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  // === FETCH STAKING PARAMS ===
  const { data: stakingParams } = useQuery({
    queryKey: queryKeys.staking.params(),
    enabled: open,
    queryFn: fetchStakingParams,
    staleTime: 300_000, // 5 minutes
  })

  const unbondingDuration = useMemo(() => {
    return formatDurationString(stakingParams?.unbonding_time)
  }, [stakingParams])

  const delegation = useMemo(() => {
    const delegationResponse = delegationData?.delegation_responses?.[0]
    const shares = delegationResponse?.delegation?.shares
    const validator = delegationResponse?.delegation?.validator_address ?? ''

    if (!shares || !validator) return null

    return {
      validator,
      shares,
      balanceAtomic: delegationResponse?.balance?.amount,
    }
  }, [delegationData])

  const hasDelegation = isPositiveDecimalAmount(delegation?.shares)

  // === TX ===
  const undelegateTx = useMutation({
    mutationKey: queryKeys.tx.undelegate(nftAddress),
    mutationFn: async () => {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      if (!delegation || !hasDelegation) {
        throw new Error('No delegation found')
      }

      const msg = buildUndelegateNftMsg({
        creator: address,
        address: nftAddress,
        validator: delegation.validator,
        share: delegation.shares,
      })

      const txHash = await signAndBroadcast([msg])
      return txHash
    },
    onSuccess: txHash => {
      emitter.emit('success', {
        title: 'Undelegation sent',
        message: `${formatAddress(txHash)}`,
      })
      invalidateNftScope(queryClient, nftAddress)
      onClose()
    },
    onError: error => {
      ErrorHandler.process(UndelegateError.fromUnknown(error))
    },
  })

  const isSubmitting = undelegateTx.isPending

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isSubmitting && onClose()}
      title={<span className='text-foreground'>Undelegate NFT</span>}
      content={
        <form
          onSubmit={event => {
            event.preventDefault()
            undelegateTx.mutate()
          }}
          className='text-foreground mt-4 flex flex-col gap-4 p-1'
        >
          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>NFT Address</span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {formatAddress(nftAddress, 20, 15)}
              </span>
              <div className='ml-auto'>
                <CopyWithTooltip value={nftAddress} />
              </div>
            </div>
          </div>

          {delegation && (
            <div className='flex flex-col gap-1'>
              <span className='text-muted-foreground text-xs'>Validator</span>
              <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
                <span className='muted-foreground text-sm'>
                  {formatAddress(delegation.validator, 20, 15)}
                </span>
                <div className='ml-auto'>
                  <CopyWithTooltip value={delegation.validator || ''} />
                </div>
              </div>
            </div>
          )}

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Amount</span>
            <div className='bg-input text-foreground flex min-h-12 items-center rounded-lg px-3 py-2 text-sm break-all'>
              {isLoading
                ? '…'
                : delegation
                  ? `${formatBridgeAtomic(delegation.balanceAtomic ?? '0')} BRIDGE`
                  : 'No active delegation found'}
            </div>
            {delegation && !hasDelegation && (
              <span className='text-xs text-red-400'>
                No delegated shares available for this NFT
              </span>
            )}
          </div>

          {unbondingDuration && hasDelegation && (
            <div className='flex flex-col gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3'>
              <div className='flex items-center gap-2'>
                <AlertTriangle className='size-4 text-yellow-500' />
                <span className='text-sm font-semibold text-yellow-500'>
                  Unbonding Period
                </span>
              </div>
              <p className='text-muted-foreground text-xs'>
                If you undelegate, your funds will be locked for
                <span className='text-foreground font-semibol px-1'>
                  {unbondingDuration}
                </span>
                before they become available for withdrawal.
              </p>
            </div>
          )}
        </form>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isSubmitting}
          >
            Close
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={isSubmitting || !hasDelegation || isLoading}
            onClick={() => undelegateTx.mutate()}
          >
            {isSubmitting ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Undelegating…
              </span>
            ) : (
              'Undelegate'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-md' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}
